import { NotFoundElement, ForbiddenElement, ToastProvider } from '@components';
import { PathnameEnum, UserRoleEnum } from '@constant';
import {
  ActionGenreOutputPage,
  BodyPartOutputPage,
  EtcOutputPage,
  FacialExpressionOutputPage,
  MainPage,
  AdultsOnlyOutputPage,
  StandardOutputPage,
  LoginPage,
  UsedImageListPage,
  AdultsOnlyImageListPage,
  ImageUpsertPage,
  CollectionListPage,
  CollectionImageListPage,
  StandardImageListPage,
  IssueReportListPage,
  ThreeDOutputPage,
  DeletedImageListPage,
  UserListPage,
  UserCreatePage,
  UserEditPage
} from '@pages';
import { useSelector } from '@stores';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

// 로그인하지 않은 사용자의 접근을 제한하고 로그인 페이지로 리디렉트
const PublicRoute = () => {
  const location = useLocation();

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path={PathnameEnum.LOGIN_PAGE} element={<LoginPage />} />
      {/* 원래 가려던 페이지를 state에 저장하여 로그인 후 해당 페이지로 이동하도록 설정 */}
      <Route path="*" element={<Navigate to={PathnameEnum.LOGIN_PAGE} replace state={{ from: location }} />} />
    </Routes>
  );
};

// 로그인한 사용자만 접근 가능한 페이지
const RestrictRoute = () => {
  const userRole = useSelector((state) => state.auth.role);

  // 권한 체크 함수 - 일반 회원 외 다른 권한만 어드민에 접근 가능하도록 함
  const canAccessAdminPages = () => {
    return (
      userRole === UserRoleEnum['루트 관리자'] ||
      userRole === UserRoleEnum['최고 관리자'] ||
      userRole === UserRoleEnum['데이터 관리자']
    );
  };

  // 권한 체크 함수 - 루트관리자 또는 최고관리자만 회원관리 페이지들에 접근 가능하도록 함
  const canAccessUserManagement = () => {
    return userRole === UserRoleEnum['루트 관리자'] || userRole === UserRoleEnum['최고 관리자'];
  };

  return (
    <Routes>
      <Route path="/" element={<Navigate to={PathnameEnum.MAIN_PAGE} replace />} />
      <Route path={PathnameEnum.MAIN_PAGE} element={<MainPage />} />
      <Route path={PathnameEnum.LOGIN_PAGE} element={<Navigate to={PathnameEnum.MAIN_PAGE} replace />} />

      {/* 검색 결과 페이지 */}
      <Route path={PathnameEnum.STANDARD_OUTPUT_PAGE} element={<StandardOutputPage />} />
      <Route path={PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE} element={<AdultsOnlyOutputPage />} />
      <Route path={PathnameEnum.ACTION_GENRE_OUTPUT_PAGE} element={<ActionGenreOutputPage />} />
      <Route path={PathnameEnum.BODY_PART_OUTPUT_PAGE} element={<BodyPartOutputPage />} />
      <Route path={PathnameEnum.FACIAL_EXPRESSION_OUTPUT_PAGE} element={<FacialExpressionOutputPage />} />
      <Route path={PathnameEnum.ETC_OUTPUT_PAGE} element={<EtcOutputPage />} />
      <Route path={PathnameEnum.THREE_D_OUTPUT_PAGE} element={<ThreeDOutputPage />} />

      {/* 컬렉션 관련 페이지 */}
      <Route path={PathnameEnum.COLLECTION_LIST_PAGE} element={<CollectionListPage />} />
      <Route path={PathnameEnum.USED_IMAGE_LIST_PAGE} element={<UsedImageListPage />} />
      <Route path={PathnameEnum.COLLECTION_IMAGE_LIST_PAGE} element={<CollectionImageListPage />} />

      {/* 어드민 관련 페이지 - 권한 체크 후 ForbiddenElement 또는 실제 컴포넌트 */}
      <Route
        path={PathnameEnum.ADMIN_ROOT}
        element={
          canAccessAdminPages() ? (
            <Navigate to={PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE} replace />
          ) : (
            <ForbiddenElement />
          )
        }
      />
      <Route
        path={PathnameEnum.ADMIN_IMAGE_LIST_ROOT}
        element={
          canAccessAdminPages() ? (
            <Navigate to={PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE} replace />
          ) : (
            <ForbiddenElement />
          )
        }
      />
      <Route
        path={PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE}
        element={canAccessAdminPages() ? <StandardImageListPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_ADULTS_ONLY_IMAGE_LIST_PAGE}
        element={canAccessAdminPages() ? <AdultsOnlyImageListPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_IMAGE_UPLOAD_PAGE}
        element={canAccessAdminPages() ? <ImageUpsertPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_IMAGE_EDIT_PAGE}
        element={canAccessAdminPages() ? <ImageUpsertPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_ISSUE_REPORT_LIST_ROOT}
        element={
          canAccessAdminPages() ? (
            <Navigate to={PathnameEnum.ADMIN_OPEN_ISSUE_REPORT_LIST} replace />
          ) : (
            <ForbiddenElement />
          )
        }
      />
      <Route
        path={PathnameEnum.ADMIN_OPEN_ISSUE_REPORT_LIST}
        element={canAccessAdminPages() ? <IssueReportListPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_RESOLVED_ISSUE_REPORT_LIST}
        element={canAccessAdminPages() ? <IssueReportListPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_DELETED_IMAGE_LIST_PAGE}
        element={canAccessAdminPages() ? <DeletedImageListPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_USER_LIST_PAGE}
        element={canAccessUserManagement() ? <UserListPage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_USER_CREATE_PAGE}
        element={canAccessUserManagement() ? <UserCreatePage /> : <ForbiddenElement />}
      />
      <Route
        path={PathnameEnum.ADMIN_USER_EDIT_PAGE}
        element={canAccessUserManagement() ? <UserEditPage /> : <ForbiddenElement />}
      />

      {/* 존재하지 않는 경로 */}
      <Route path="*" element={<NotFoundElement />} />
    </Routes>
  );
};

// 로그인 상태를 확인하고, 원래 가려던 페이지로 이동시키는 컴포넌트
const AppRoutes = () => {
  const isLogin = useSelector((state) => state.auth.isLogin);

  // 로그인 후 이동할 경로 설정 (원래 가려던 경로가 있으면 해당 경로로 이동)
  const location = useLocation();
  const redirectPath = location.state?.from?.pathname;

  if (!isLogin) {
    return <PublicRoute />;
  }

  // RestrictRoute를 먼저 렌더링하여 정상적으로 페이지가 보이도록 처리
  return (
    <>
      <RestrictRoute />
      {redirectPath && <Navigate to={redirectPath} replace />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
